import { Box, Typography } from '@mui/material';
import Lottie from 'lottie-react';
import React, { useState } from 'react';
import { PERSONAL_BENEFITS } from 'shared/constants/subscriptions/plans';

import blueWave from '@/assets/animations/blue-wave.json';
import { EquinetPremium, RightArrowIcon } from '@/assets/svg';
import { Button } from '@/components';
import { useSubscriptionContext } from '@/context/SubscriptionContext';
import { useTrialEligibility } from '@/hooks/useTrialEligibility';
import { t } from '@/services/translations/config';
import { COLOR } from '@/theme/colors';

import { UnlockPremiumModal } from '../../..';
import { getFormattedPrice } from '../../utils';
import { PlanBenefits } from '../PlanBenefits';
import { planStyles } from '../planStyles';

const PremiumPlan: React.FC = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { webPlans } = useSubscriptionContext();
    const isFreeTrialEligible = useTrialEligibility();

    const openDialog = () => {
        setIsModalOpen(true);
    };

    const buttonText = isFreeTrialEligible
        ? t('SelectSubscription:button:confirm:start_trial')
        : t('SelectSubscription:button:confirm:subscribe');

    return (
        <>
            {isModalOpen && (
                <UnlockPremiumModal
                    title={t('PremiumPage:promotionBanner:premium:title')}
                    description={t(
                        'PremiumPage:promotionBanner:premium:description',
                    )}
                    logo={
                        <EquinetPremium
                            width={210}
                            color={COLOR.deepCerulean}
                        />
                    }
                    color={COLOR.deepCerulean}
                    close={() => setIsModalOpen(false)}
                />
            )}

            <Box sx={planStyles.wrapper}>
                <Box
                    sx={{
                        ...planStyles.container,
                        background: `linear-gradient(180deg, ${COLOR.linkWater}, ${COLOR.regentStBlue})`,
                    }}
                >
                    <Lottie
                        className="absolute right-0 bottom-0 left-0 opacity-30 -z-1"
                        animationData={blueWave}
                    />
                    <Box
                        sx={{
                            ...planStyles.box,
                            backgroundColor: COLOR.white,
                            border: `1px solid ${COLOR.deepCerulean}`,
                        }}
                    >
                        <Typography color={COLOR.deepCerulean} fontSize={10}>
                            {t('PremiumPage:subscriptionPlans:premium:label')}
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                        }}
                    >
                        <EquinetPremium color={COLOR.deepCerulean} />
                    </Box>

                    <Typography fontSize={12} color={COLOR.slateGray}>
                        {t('PremiumPage:subscriptionPlans:premium:description')}
                    </Typography>

                    <Box>
                        <Typography fontSize={24} fontWeight={700}>
                            {getFormattedPrice(webPlans.premium)}
                        </Typography>
                        <Typography fontSize={11} color={COLOR.slateGray}>
                            /
                            {t(
                                'PremiumPage:subscriptionPlans:premium:priceDescription',
                            )}
                        </Typography>
                    </Box>

                    <Button
                        sx={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: COLOR.white,
                            background: `linear-gradient(180deg, ${COLOR.royalBlue}, ${COLOR.deepCerulean})`,
                            borderRadius: '50px',
                            borderColor: COLOR.frenchPass,
                            mt: 'auto',
                            '&:hover': {
                                opacity: 0.8,
                            },
                        }}
                        variant="outlined"
                        endIcon={<RightArrowIcon color={COLOR.white} />}
                        caption={buttonText}
                        onClick={openDialog}
                    />
                </Box>

                <PlanBenefits
                    benefits={PERSONAL_BENEFITS}
                    title={t('PremiumPage:subscriptionPlans:premium:benefits')}
                />
            </Box>
        </>
    );
};

export default PremiumPlan;
